//path: src\modules\telemetry\newRelicBrowser.ts

import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { getConsent } from '../cookieConsent/storage';
import { runtimeEnv } from '@/lib/runtimeEnv';
import { BACKEND_URL } from '@/infrastructure/clients/backendEnv';
import { deviceId, onDeviceIdChange } from '@/infrastructure/clients/BackendClient';
import { agentInit, createDeviceAttribute, createEndUser, recordAssetDownloads, type NrBrowserConfig, type ResourceTimingEntry } from './newRelicOptions';
import { setFallbackSink } from '@/modules/compressedModels/fallback';

// New Relic Browser agent: starts once, only when configured and analytics consent is granted.

const EU_BEACON = 'bam.eu01.nr-data.net';
const ACCOUNT_ID = '8293408';

function readConfig(): NrBrowserConfig | null {
  const env = runtimeEnv;
  const licenseKey = env.VITE_NR_BROWSER_LICENSE_KEY;
  const applicationID = env.VITE_NR_BROWSER_APP_ID;
  if (!licenseKey || !applicationID) return null;
  return {
    licenseKey,
    applicationID,
    environment: env.VITE_NR_BROWSER_ENV ?? 'production',
  };
}

let started = false;

// Who is signed in, as the agent's user id (enduser.id): set by the auth layer, kept until the agent starts
const endUser = createEndUser();

/** The signed-in user's unique id, or null once they sign out. Safe to call before the agent starts. */
export function setNewRelicUser(id: string | null): void {
  endUser.set(id);
}

/** The user id the agent has, or will be handed as it starts (enduser.id); null for none. */
export const newRelicUser = (): string | null => endUser.current();

// The device id every backend call carries (X-Lureo-Device), as lureo.device.id: followed from the first call on, and
// kept until the agent starts
const device = createDeviceAttribute();
onDeviceIdChange(id => device.set(id));

/** Start the agent once, if configured and analytics consent is granted. */
export function initNewRelicBrowser(): void {
  if (started) return;
  if (typeof window === 'undefined') return;
  if (!getConsent()?.analytics) return;

  const config = readConfig();
  if (!config) return;

  const agent = new BrowserAgent({
    info: {
      beacon: EU_BEACON,
      errorBeacon: EU_BEACON,
      licenseKey: config.licenseKey,
      applicationID: config.applicationID,
      sa: 1,
    },
    loader_config: {
      accountID: ACCOUNT_ID,
      trustKey: ACCOUNT_ID,
      agentID: config.applicationID,
      licenseKey: config.licenseKey,
      applicationID: config.applicationID,
    },
    // The backend's calls traced across origins: their spans carry the region that answered each one
    init: agentInit(BACKEND_URL),
  });

  // Tag every event with the environment.
  agent.setCustomAttribute('environment', config.environment, true);

  // And with the device the backend is told on every call, as its spans carry it (lureo.device.id), so a browser session
  // joins the backend's device rows by field; read now, in case no call has read it yet
  device.set(deviceId());
  device.attach(agent);

  // The signed-in user's unique id on every event (enduser.id), as the backend's spans carry it
  endUser.attach(agent);

  // A compressed model that gave way to its original: an asset_compressed_fallback event of its own
  setFallbackSink('newrelic', (event, properties) => agent.recordCustomEvent(event, properties));

  // Asset and model downloads from the CDN, timed one by one: the agent's own resource timings would time every file
  if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('resource')) {
    recordAssetDownloads(agent, each => {
      new PerformanceObserver(list => list.getEntries().forEach(entry => each(entry as unknown as ResourceTimingEntry)))
        .observe({ type: 'resource', buffered: true });
    });
  }

  started = true;
}
